// @ts-nocheck

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Image } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { ensureStorageBuckets } from '@/lib/storage-utils';

interface FeaturedImageUploadProps {
  currentImageUrl?: string | null;
  onImageChange: (imageUrl: string | null) => void;
}

const FeaturedImageUpload: React.FC<FeaturedImageUploadProps> = ({
  currentImageUrl,
  onImageChange
}) => {
  const [uploading, setUploading] = useState(false);
  
  // Construct full URL for preview if we have a filename
  const getPreviewUrl = (filename: string | null | undefined): string => {
    if (!filename) return '';
    // If it's already a full URL, return as is
    if (filename.startsWith('http')) return filename;
    // Construct the full URL for preview using the correct bucket name
    return `https://pvaumvowzncvrdkkvnqq.supabase.co/storage/v1/object/public/event-featured/${filename}`;
  };

  const previewUrl = getPreviewUrl(currentImageUrl);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      const file = event.target.files?.[0];
      if (!file) {
        console.log('📄 No file selected');
        return;
      }

      console.log('📄 File selected:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // Check authentication status
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('🔐 Auth check:', {
        hasSession: !!session,
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        role: session?.user?.role,
        error: sessionError
      });

      if (!session) {
        toast.error('You must be logged in to upload images');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        console.error('❌ Invalid file type:', file.type);
        toast.error('Please select a valid image file');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        console.error('❌ File too large:', file.size);
        toast.error('File size must be less than 5MB');
        return;
      }

      console.log('✅ File validation passed');

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      console.log('📤 Starting upload to bucket "event-featured" with filename:', fileName);

      // Upload to Supabase Storage using the correct bucket name
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('event-featured')
        .upload(fileName, file, { 
          upsert: true,
          contentType: file.type 
        });

      if (uploadError) {
        console.error('❌ Upload error details:', {
          message: uploadError.message,
          error: uploadError,
          errorString: JSON.stringify(uploadError, null, 2),
          fileName,
          fileSize: file.size,
          fileType: file.type,
          bucket: 'event-featured'
        });
        
        if (uploadError.message?.includes('row-level security')) {
          toast.error('Permission denied. Please ensure you are logged in as an admin.');
        } else if (uploadError.message?.includes('Payload too large')) {
          toast.error('File is too large. Maximum size is 5MB.');
        } else {
          toast.error(`Failed to upload image: ${uploadError.message || 'Unknown error'}`);
        }
        return;
      }

      if (!uploadData) {
        console.error('❌ No upload data returned');
        toast.error('Upload failed: No data returned');
        return;
      }

      console.log('✅ Upload successful:', uploadData);
      
      // Pass only the filename to be stored in the database
      onImageChange(fileName);
      toast.success('Featured image uploaded successfully!');

    } catch (error) {
      console.error('💥 Unexpected error uploading image:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    onImageChange(null);
    toast.success('Featured image removed');
  };

  return (
    <div className="space-y-4">
      <Label htmlFor="featured-image">Featured Image (Optional)</Label>
      
      {previewUrl ? (
        <div className="relative">
          <img
            src={previewUrl}
            alt="Featured image preview"
            className="w-full max-w-md h-48 object-cover rounded-lg border"
            onError={(e) => {
              console.error('❌ Failed to load image:', previewUrl);
              e.currentTarget.style.display = 'none';
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemoveImage}
          >
            <X className="h-4 w-4" />
          </Button>
          
          {/* Allow replacing the image */}
          <div className="mt-2">
            <div className="relative">
              <Input
                id="featured-image-replace"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Button type="button" variant="outline" disabled={uploading} className="w-full">
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Replace Image'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <Image className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-sm text-gray-500 mb-4">
            Upload a featured image for your event (optional)
          </p>
          <div className="relative">
            <Input
              id="featured-image"
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <Button type="button" disabled={uploading}>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Choose Image'}
            </Button>
          </div>
        </div>
      )}
      
      <p className="text-xs text-gray-500">
        Square image used on event cards and the sidebar of your event page. Recommended: 600×600. Max file size: 5MB. Accepted formats: JPG, PNG, GIF, WebP
      </p>
    </div>
  );
};

export default FeaturedImageUpload;